import type { IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import type { InteractionRecord } from '@/shared/analytics/AnalyticsTypes';
import type { Classification } from '../classification/ClassificationService';
import { ACTIVITY_SCAN_LIMIT, policyFieldName, toClassifiedItem } from './GovernancePolicyData';
import { flagTurnSeverity, issueKey, issueRef, type GovernanceIssue } from './PolicyIssues';
import { concernSeverity, isConcerningValue } from './PolicyVerdict';
import { humanizeFlag, SENSITIVITY_SCAN_LIMIT } from './SensitivityAlerts';

/**
 * Load the governance issue feed — one issue per flagged turn from both sources
 * (policy-verdict hits and sensitivity-flag hits), over all time (the issue queue
 * is a work list of every open finding, not date-scoped). The single source of
 * truth shared by the Issues tab (its rows) and the landing's Issues badge (its
 * count), so the two always agree.
 */
export async function loadGovernanceIssueFeed(gateway: IAnalyticsGateway, policies: Classification[]): Promise<GovernanceIssue[]> {
    const withField = policies.filter((p) => policyFieldName(p) !== null);
    const fieldNames = withField.map((p) => policyFieldName(p)).filter((n): n is string => n !== null);
    const [policyRecords, sensitivityRecords] = await Promise.all([
        fieldNames.length > 0
            ? gateway.records({ source: 'interactions', dimensionKeys: fieldNames, orderBy: { column: 'timestamp', direction: 'desc' }, limit: ACTIVITY_SCAN_LIMIT, ignoreGlobalFilters: true })
            : Promise.resolve({ records: [], totalMatched: 0 }),
        gateway.records({ source: 'interactions', hasSensitivityFlag: true, orderBy: { column: 'timestamp', direction: 'desc' }, limit: SENSITIVITY_SCAN_LIMIT, ignoreGlobalFilters: true }),
    ]);
    return [...policyTurnIssues(policyRecords.records, withField), ...sensitivityTurnIssues(sensitivityRecords.records)];
}

/** One issue per turn a policy gave a concerning verdict. */
function policyTurnIssues(records: InteractionRecord[], policies: Classification[]): GovernanceIssue[] {
    const issues: GovernanceIssue[] = [];
    for (const record of records) {
        for (const policy of policies) {
            const fieldName = policyFieldName(policy);
            if (fieldName === null) continue;
            const value = record.dimensions[fieldName]?.value;
            if (value === undefined) continue;
            if (!isConcerningValue(policy.outputType, value, policy.outputConfig)) continue;
            const item = toClassifiedItem(record, policy, fieldName);
            const ref = issueRef(policy.id, record.id);
            issues.push({
                kind: 'policy',
                ref,
                key: issueKey('policy', ref),
                interactionId: record.id,
                sessionId: item.sessionId,
                turnIndex: item.turnIndex,
                title: policy.name,
                verdict: item.output,
                severity: concernSeverity(policy.outputType, value, policy.outputConfig),
                person: item.person,
                department: item.department,
                timestampIso: item.timestampIso,
                confidence: item.confidence,
            });
        }
    }
    return issues;
}

/** One issue per turn carrying a sensitivity flag (one row per flag on the turn). */
function sensitivityTurnIssues(records: InteractionRecord[]): GovernanceIssue[] {
    const issues: GovernanceIssue[] = [];
    for (const record of records) {
        for (const flag of record.sensitivityFlags) {
            const ref = issueRef(flag, record.id);
            issues.push({
                kind: 'sensitivity',
                ref,
                key: issueKey('sensitivity', ref),
                interactionId: record.id,
                sessionId: record.sessionId,
                turnIndex: record.turnIndex,
                title: humanizeFlag(flag),
                verdict: humanizeFlag(flag),
                severity: flagTurnSeverity(flag),
                person: record.userId.length > 0 ? record.userId : '—',
                department: record.dimensions['user_department']?.value ?? '—',
                timestampIso: record.timestamp,
                confidence: null,
            });
        }
    }
    return issues;
}
